import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Lock, CheckCircle2, XCircle, Phone, Briefcase } from "lucide-react";

function OnboardForm() {
  const qc = useQueryClient();
  const { data: branches = [] } = useQuery({
    queryKey: ["branches"],
    queryFn: async () => (await apiClient.get("/branches")).data,
  });
  const [f, setF] = useState({ email: "", password: "", full_name: "", role: "COACH", phone: "", branch_ids: [] });
  const create = useMutation({
    mutationFn: async () => (await apiClient.post("/users/onboard-employee", f)).data,
    onSuccess: (d) => {
      const n = (d.linked_referrals || []).length;
      toast.success(n ? `${d.user.full_name} onboarded · ${n} job referral(s) auto-verified 🎉` : `${d.user.full_name} onboarded`);
      qc.invalidateQueries({ queryKey: ["referral-holds"] });
      setF({ email: "", password: "", full_name: "", role: "COACH", phone: "", branch_ids: [] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const toggleBranch = (id) => setF({ ...f, branch_ids: f.branch_ids.includes(id) ? f.branch_ids.filter(b => b !== id) : [...f.branch_ids, id] });
  const canSubmit = f.email && f.password && f.full_name && f.role;
  return (
    <Panel data-testid="onboard-panel">
      <PanelHeader title="Onboard New Employee" subtitle="Phone number is matched against pending job referrals to auto-verify" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Full Name</Label>
          <Input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="onb-name" className="bg-black border-zinc-800 rounded-sm mt-1" /></div>
        <div><Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Email</Label>
          <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="onb-email" className="bg-black border-zinc-800 rounded-sm mt-1" /></div>
        <div><Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Phone (used for referral verification)</Label>
          <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="onb-phone" placeholder="+91XXXXXXXXXX" className="bg-black border-zinc-800 rounded-sm mt-1" /></div>
        <div><Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Temporary Password</Label>
          <Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="onb-pass" className="bg-black border-zinc-800 rounded-sm mt-1" /></div>
        <div><Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Role</Label>
          <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
            <SelectTrigger data-testid="onb-role" className="bg-black border-zinc-800 rounded-sm mt-1"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="COACH">Coach</SelectItem><SelectItem value="ADMIN">Branch Admin</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Branches</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {branches.map((b) => (
              <button key={b.id} type="button" onClick={() => toggleBranch(b.id)}
                data-testid={`onb-branch-${b.id}`}
                className={`text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded-sm border ${f.branch_ids.includes(b.id) ? "bg-volt text-black border-volt" : "bg-zinc-950 text-zinc-400 border-zinc-800 hover:border-zinc-700"}`}>
                {b.name}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={() => create.mutate()} disabled={!canSubmit || create.isPending} data-testid="onboard-btn"
          className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
          <UserPlus size={14} className="mr-1" /> Onboard
        </Button>
      </div>
    </Panel>
  );
}

function HoldsPanel() {
  const qc = useQueryClient();
  const { data: holds = [] } = useQuery({
    queryKey: ["referral-holds"],
    queryFn: async () => (await apiClient.get("/job-referrals/pending-holds")).data,
  });
  const [reason, setReason] = useState({});
  const decide = useMutation({
    mutationFn: async ({ id, action }) => (await apiClient.post(`/job-referrals/${id}/hold-decision`, { action, reason: reason[id] || null })).data,
    onSuccess: (_d, v) => { toast.success(v.action === "RELEASE" ? "Released — REPS credited" : "Held back permanently"); qc.invalidateQueries({ queryKey: ["referral-holds"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <Panel data-testid="holds-panel">
      <PanelHeader title={`Job Referral 6-Month Holds · ${holds.length}`}
        subtitle="Verified hires. Release credits REPS to referrer. Hold Back = forfeit (used if new employee quits/is terminated)." />
      {holds.length === 0 ? (
        <div className="text-zinc-500 text-sm py-6 text-center">No pending referral holds.</div>
      ) : (
        <div className="space-y-2">
          {holds.map((h) => (
            <div key={h.id} className="bg-zinc-950 border border-zinc-900 rounded-sm p-3" data-testid={`hold-${h.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Briefcase size={12} className="text-volt" />
                    <span className="font-semibold">{h.job_title}</span>
                    <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">→ Referrer: {h.referrer_name}</span>
                  </div>
                  <div className="text-xs text-zinc-400 mt-1 flex items-center gap-3 flex-wrap">
                    <span><Phone size={10} className="inline mr-1" />{h.candidate_phone} · {h.candidate_name}</span>
                    <span className="flex items-center gap-1"><Lock size={10} /> Release on {h.reps_hold_release_at?.slice(0, 10) || "—"}</span>
                    {h.ready_to_release && <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-green-400">Ready ✓</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-3xl font-black text-volt">{h.reps_pending_amount}</div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">REPS in escrow</div>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Input placeholder="Reason if holding back" value={reason[h.id] || ""} onChange={(e) => setReason({ ...reason, [h.id]: e.target.value })}
                  data-testid={`hold-reason-${h.id}`} className="bg-black border-zinc-800 rounded-sm flex-1 min-w-[200px]" />
                <Button size="sm" onClick={() => decide.mutate({ id: h.id, action: "RELEASE" })}
                  data-testid={`release-${h.id}`} className="bg-green-500 hover:bg-green-400 text-black rounded-sm text-[10px] uppercase tracking-[0.14em]">
                  <CheckCircle2 size={12} className="mr-1" /> Release
                </Button>
                <Button size="sm" variant="outline" onClick={() => decide.mutate({ id: h.id, action: "HOLD_BACK" })}
                  data-testid={`hold-back-${h.id}`} className="border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-sm text-[10px] uppercase tracking-[0.14em]">
                  <XCircle size={12} className="mr-1" /> Hold Back
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

export default function StaffPage() {
  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="staff-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">People Ops</div>
        <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Staff & Referral Holds</h1>
        <div className="text-zinc-500 text-sm mt-1">Onboard staff · verify hires · release or hold back referral REPS.</div>
      </div>
      <OnboardForm />
      <HoldsPanel />
    </div>
  );
}
